import React, { useEffect, useState } from 'react';
import { FiCheck, FiHome } from 'react-icons/fi';
import { buildPatientPortalUrl } from '../../services/patientPortalUrl';
import './LabCheckoutConfirmed.css';

const NEXT_STEPS = {
  at_home_phlebotomy: [
    { title: 'Order processing', desc: 'Your lab order has been accepted for processing.' },
    { title: 'Booking link sent', desc: "We'll email you a link to book your phlebotomy visit when it is ready." },
    { title: 'Sample collected', desc: 'A phlebotomist visits your address at your scheduled time.' },
    { title: 'Results in your portal', desc: 'Typically 1–3 days after your sample reaches the lab.' },
  ],
  walk_in_test: [
    { title: 'Order processing', desc: 'Your lab order has been accepted for processing.' },
    { title: 'Requisition ready', desc: "Your requisition will be emailed to you and available in your patient portal." },
    { title: 'Visit a draw site', desc: 'Bring your ID and requisition to any of the nearby draw sites.' },
    { title: 'Results in your portal', desc: 'Typically 1–2 days after your sample reaches the lab.' },
  ],
  testkit: [
    { title: 'Order processing', desc: 'Your lab order has been accepted for processing.' },
    { title: 'Your kit ships', desc: 'Track it from your patient portal — it arrives in a few days.' },
    { title: 'Collect & mail back', desc: 'Use the kit and the prepaid return label, then drop it in the mail.' },
    { title: 'Results in your portal', desc: 'Typically 1–2 days after your sample reaches the lab.' },
  ],
};

const DEFAULT_NEXT_STEPS = [
  { title: 'Order processing', desc: 'Your lab order has been accepted for processing.' },
  { title: 'Next steps', desc: "You'll receive an email with next steps once your order is authorized." },
  { title: 'Results in your portal', desc: 'Results will appear in your patient portal when ready.' },
];

export default function LabCheckoutConfirmed({
  confirmedOrder,
  lab,
  selectedMethod,
  logoInitial,
  logoName,
  logoUrl,
  patientPortalDomain,
}) {
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  useEffect(() => {
    setBrandLogoFailed(false);
  }, [logoUrl]);
  const isSuccess = confirmedOrder.state === 'success';
  const isPaymentFailed = confirmedOrder.state === 'payment_failed';
  const method = selectedMethod?.method || '';
  const nextSteps = NEXT_STEPS[method] || DEFAULT_NEXT_STEPS;
  const methodLabel = selectedMethod?.label || '';
  const total = confirmedOrder.total;

  return (
    <div className="lcw">
      {/* Header */}
      <header className="lcw-header">
        <div className="lcw-header-inner">
          <div className="lcw-logo">
            {logoUrl && !brandLogoFailed ? (
              <img
                className="lcw-logo-image"
                src={logoUrl}
                alt={logoName}
                onError={() => setBrandLogoFailed(true)}
              />
            ) : (
              <div className="lcw-logo-icon" aria-hidden="true">{logoInitial}</div>
            )}
          </div>
        </div>
      </header>

      <div className="lcw-main lcw-confirmed">
        {isSuccess ? (
          <>
            <div className="lcw-confirmed-success">
              <FiCheck size={28} color="#16a34a" />
            </div>
            <h1 className="lcw-confirmed-title">Order placed</h1>
            <p className="lcw-confirmed-body">
              Thanks, your lab order has been submitted. We've emailed your confirmation.
            </p>

            <div className="lcw-card lcw-card--left lcw-card--spaced">
              <div className="lcw-card-body">
                <div className="lcw-review">
                  <div>
                    <div className="lcw-review-name">{lab?.name || 'Lab Panel'}</div>
                    {methodLabel && <div className="lcw-review-sub">{methodLabel}</div>}
                    {confirmedOrder.orderNumber && (
                      <div className="lcw-order-id">
                        {confirmedOrder.orderNumber}
                      </div>
                    )}
                  </div>
                  {total != null && <div className="lcw-review-price">${Number(total).toFixed(2)}</div>}
                </div>
              </div>
            </div>

            <div className="lcw-card lcw-card--left lcw-card--lg-spaced">
              <div className="lcw-card-body">
                <div className="lcw-section-title lcw-next-steps-title">What happens next</div>
                {nextSteps.map((step, i) => (
                  <div key={i} className="lcw-next-step">
                    {i < nextSteps.length - 1 && <div className="lcw-next-step-line" />}
                    <div className={`lcw-next-step-index ${i === 0 ? 'is-current' : ''}`}>
                      {i + 1}
                    </div>
                    <div>
                      <div className="lcw-next-step-title">{step.title}</div>
                      <div className="lcw-next-step-desc">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              className="lcw-btn-continue-only lcw-btn-icon"
              onClick={() => window.location.assign(buildPatientPortalUrl(patientPortalDomain))}
            >
              <FiHome size={16} /> Go to your patient portal
            </button>
          </>
        ) : (
          <>
            <div className="lcw-confirmed-icon">{isPaymentFailed ? '⚠️' : '❌'}</div>
            <h1 className="lcw-confirmed-title lcw-confirmed-title--error">
              {isPaymentFailed ? 'Payment issue' : 'Order failed'}
            </h1>
            <p className="lcw-confirmed-body lcw-confirmed-body--error">{confirmedOrder.message}</p>
            {confirmedOrder.orderNumber && (
              <div className="lcw-card lcw-card--left lcw-card--spaced">
                <div className="lcw-card-body">
                  <div className="lcw-order-ref-label">Order reference</div>
                  <div className="lcw-order-ref-value">{confirmedOrder.orderNumber}</div>
                </div>
              </div>
            )}
            <div className="lcw-card lcw-card--left">
              <div className="lcw-card-body lcw-muted-copy">
                Please contact support and reference your order number above. Do not resubmit your order.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
